import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription } from "./ui/alert";
import { LogIn, User, Shield, Zap, Eye, EyeOff, Users } from "lucide-react";

interface LoginProps {
  onLogin: (userData: {
    username: string;
    role: string;
    fullName: string;
    avatar: string;
  }) => void;
}

export function Login({ onLogin }: LoginProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Mock user accounts for demonstration
  const accounts = [
    {
      username: "admin",
      password: "admin123",
      role: "System Admin",
      fullName: "John Anderson",
      avatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    },
    {
      username: "manager",
      password: "manager123",
      role: "Space Manager",
      fullName: "Sarah Wilson",
      avatar:
        "https://images.unsplash.com/photo-1494790108755-2616b612b605?w=150&h=150&fit=crop&crop=face",
    },
    {
      username: "member",
      password: "member123",
      role: "Member",
      fullName: "John Doe",
      avatar:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    },
  ];

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const account = accounts.find(
      (acc) => acc.username === username && acc.password === password
    );

    if (account) {
      onLogin({
        username: account.username,
        role: account.role,
        fullName: account.fullName,
        avatar: account.avatar,
      });
    } else {
      setError("Invalid username or password");
    }

    setIsLoading(false);
  };

  const handleQuickLogin = async (account: (typeof accounts)[0]) => {
    setIsLoading(true);
    setError("");

    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    onLogin({
      username: account.username,
      role: account.role,
      fullName: account.fullName,
      avatar: account.avatar,
    });

    setIsLoading(false);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "System Admin":
        return "bg-red-100 text-red-800";
      case "Space Manager":
        return "bg-blue-100 text-blue-800";
      case "Member":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "System Admin":
        return <Shield className="h-4 w-4" />;
      case "Space Manager":
        return <Users className="h-4 w-4" />;
      case "Member":
        return <User className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center">
              <LogIn className="h-8 w-8 text-primary-foreground" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold">Coworking Hub</h1>
          <p className="text-muted-foreground">Sign in to access the system</p>
        </div>

        {/* Login Form */}
        <Card>
          <CardHeader>
            <CardTitle data-testid="login-form-title">Sign In</CardTitle>
            <CardDescription data-testid="login-form-description">
              Enter your credentials to access the system
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    aria-label="Input password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription data-testid="alert-description">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    Sign In
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Quick Login */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Quick Login
            </CardTitle>
            <CardDescription>
              Choose an account for quick access (Demo purposes)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {accounts.map((account) => (
                <Button
                  key={account.username}
                  variant="outline"
                  className="w-full justify-start h-auto p-4"
                  onClick={() => handleQuickLogin(account)}
                  disabled={isLoading}
                >
                  <div className="flex items-center space-x-3 w-full">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      {getRoleIcon(account.role)}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{account.fullName}</p>
                        <Badge
                          className={getRoleColor(account.role)}
                          variant="secondary"
                        >
                          {account.role}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        @{account.username}
                      </p>
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground">
          <p>Coworking Management System V1.4.2</p>
          <p>Sign in with your credentials or use quick login for demo</p>
        </div>
      </div>
    </div>
  );
}
